<template>
  <div class="tx-list-page">
    <v-container fluid grid-list-xl>
      <v-layout row wrap>
        <v-flex xs12>
          <div class="list-hero">
            <p class="list-crumb">Contabilidad / Tarifas</p>
            <h1 class="list-title">Tarifas</h1>
            <p class="list-subtitle">
              Precios que el sistema usa al facturar: el corretaje aduanal por
              valor CIF, y la tarifa de cada concepto de servicio. Las dos
              admiten un precio propio por cliente.
            </p>
          </div>
        </v-flex>

        <!-- Simulador: probar la tarifa antes de guardarla evita descubrir el
             error en la factura del cliente. -->
        <v-flex xs12>
          <v-card class="tx-card sim">
            <div class="sim__row">
              <div class="sim__field">
                <label class="sim__label">Valor CIF</label>
                <input
                  v-model="sim.cif"
                  type="text"
                  inputmode="decimal"
                  class="sim__input"
                  placeholder="25000"
                />
              </div>
              <div class="sim__field sim__field--small">
                <label class="sim__label">Líneas de aforo</label>
                <input
                  v-model="sim.lines"
                  type="text"
                  inputmode="numeric"
                  class="sim__input"
                  placeholder="1"
                />
              </div>
              <button class="tx-btn tx-btn--secondary" @click="simulate">
                <v-icon small class="tx-btn__icon">calculate</v-icon>
                Calcular
              </button>
              <div v-if="sim.result !== null" class="sim__result">
                <span class="sim__result-label">Tarifa</span>
                <strong class="sim__result-value">
                  B/. {{ money(sim.result) }}
                </strong>
                <span v-if="sim.rateName" class="sim__result-rate">
                  {{ sim.rateName }}
                </span>
              </div>
              <div v-else-if="sim.error" class="sim__error">
                {{ sim.error }}
              </div>
            </div>
            <!-- Desglose: el cliente no veía de dónde salía el cobro por línea.
                 Cada sumando se muestra por separado, con la línea gratis
                 explícita. -->
            <dl v-if="sim.result !== null" class="sim__detail">
              <template v-for="row in sim.detail">
                <dt :key="row.label + '-k'" class="sim__detail-label">
                  {{ row.label }}
                </dt>
                <dd :key="row.label + '-v'" class="sim__detail-value">
                  {{ row.value }}
                </dd>
              </template>
              <dt class="sim__detail-label sim__detail-label--total">Total</dt>
              <dd class="sim__detail-value sim__detail-value--total">
                B/. {{ money(sim.result) }}
              </dd>
            </dl>
            <p class="sim__hint">
              La primera línea de aforo está incluida en la tarifa; se cobra
              desde la segunda (Resolución 222/2025).
            </p>
          </v-card>
        </v-flex>

        <v-flex xs12>
          <v-card class="tx-card">
            <div class="tx-toolbar">
              <div class="tx-toolbar__info">
                <span class="tx-chip">
                  <v-icon small class="tx-chip__icon">payments</v-icon>
                  {{ activos }} tramos vigentes
                  <template v-if="rates.length !== activos">
                    ({{ rates.length - activos }} retirados)
                  </template>
                </span>
                <button
                  v-if="rates.length !== activos"
                  class="retired-link"
                  @click="showRetiredRates = !showRetiredRates"
                >
                  {{
                    showRetiredRates
                      ? 'Ocultar retirados'
                      : `Ver ${rates.length - activos} retirado(s)`
                  }}
                </button>
              </div>
              <div class="tx-toolbar__actions">
                <v-tooltip bottom>
                  <template slot="activator">
                    <button class="tx-btn tx-btn--approve" @click="openNew">
                      <v-icon small class="tx-btn__icon">add</v-icon>
                      Nuevo tramo
                    </button>
                  </template>
                  <span>
                    Agrega un rango de CIF con su tarifa. Puede ser general o de
                    un cliente
                  </span>
                </v-tooltip>
              </div>
            </div>
            <v-divider></v-divider>

            <v-card-text class="pa-0">
              <div class="table-wrap">
                <table class="tx-table">
                  <thead>
                    <tr>
                      <th>Tramo</th>
                      <th class="th-right">CIF desde</th>
                      <th class="th-right">CIF hasta</th>
                      <th class="th-right">Tarifa fija</th>
                      <th class="th-right">%</th>
                      <th class="th-right">% sobre excedente de</th>
                      <th class="th-right">Línea extra</th>
                      <th>Vigente desde</th>
                      <th class="th-actions">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- Un grupo por dueño: General primero, luego cada
                         cliente con su tarifa propia. -->
                    <template v-for="g in ratesGrouped">
                      <tr :key="g.key + '-head'" class="grp-row">
                        <td colspan="9">
                          <v-icon small class="grp-row__icon">
                            {{ g.owner === 'GENERAL' ? 'public' : 'folder' }}
                          </v-icon>
                          {{ g.name }}
                          <span
                            v-if="g.owner !== 'GENERAL'"
                            class="folder__tag"
                          >
                            {{
                              g.owner === 'LAB' ? 'Laboratorio' : 'Distribuidor'
                            }}
                          </span>
                          <span class="grp-row__count">
                            {{ g.rates.filter(r => r.active).length }}
                            vigente(s)
                          </span>
                        </td>
                      </tr>
                      <tr
                        v-for="rate in visibleRates(g)"
                        :key="rate.id"
                        :class="{ 'is-retired': !rate.active }"
                      >
                        <td class="tx-cell--strong">{{ rate.name }}</td>
                        <td class="th-right">{{ money(rate.cifFrom) }}</td>
                        <td class="th-right">
                          <span v-if="rate.cifTo === null" class="tx-muted">
                            Sin tope
                          </span>
                          <span v-else>{{ money(rate.cifTo) }}</span>
                        </td>
                        <td class="th-right">{{ money(rate.baseAmount) }}</td>
                        <td class="th-right">
                          <span v-if="rate.percent">{{ rate.percent }}</span>
                          <span v-else class="tx-muted">—</span>
                        </td>
                        <td class="th-right">
                          <span v-if="rate.percentOverAmount">
                            {{ money(rate.percentOverAmount) }}
                          </span>
                          <span v-else class="tx-muted">CIF completo</span>
                        </td>
                        <td class="th-right">{{ money(rate.perExtraLine) }}</td>
                        <td class="tx-nowrap">{{ dateOf(rate.validFrom) }}</td>
                        <td class="th-actions">
                          <div class="action-group">
                            <!-- Un tramo retirado no se edita: editarlo
                               "guardaba" pero seguía inactivo y el cálculo lo
                               ignoraba. Para cambiarlo se crea uno nuevo. -->
                            <span
                              v-if="!rate.active"
                              class="status-pill status-pill--muted"
                            >
                              Retirado
                            </span>
                            <v-tooltip bottom v-if="rate.active">
                              <template slot="activator">
                                <v-btn
                                  class="btn-detail"
                                  icon
                                  small
                                  aria-label="Editar tramo"
                                  @click="openEdit(rate)"
                                >
                                  <v-icon small>edit</v-icon>
                                </v-btn>
                              </template>
                              <span>Editar este tramo</span>
                            </v-tooltip>
                            <v-tooltip bottom v-if="rate.active">
                              <template slot="activator">
                                <v-btn
                                  class="btn-reject"
                                  icon
                                  small
                                  aria-label="Retirar tramo"
                                  @click="retire(rate)"
                                >
                                  <v-icon small>block</v-icon>
                                </v-btn>
                              </template>
                              <span>
                                Retira el tramo. No se borra: se conserva para
                                auditar con qué tarifa se facturó cada periodo
                              </span>
                            </v-tooltip>
                          </div>
                        </td>
                      </tr>
                    </template>
                    <tr v-if="!loadingRates && rates.length === 0">
                      <td colspan="9" class="empty-state">
                        No hay tramos configurados.
                      </td>
                    </tr>
                    <tr v-if="loadingRates">
                      <td colspan="9" class="empty-state">Cargando...</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
        <!-- Tarifas de los conceptos de servicio: una carpeta por cliente.
             El cliente lo pidió así: "crear folder, ponerle el nombre, abrirlo
             e ir creando cada tarifa que tengo con ese cliente". -->
        <v-flex xs12>
          <v-card class="tx-card">
            <div class="tx-toolbar">
              <div class="tx-toolbar__info">
                <span class="tx-chip">
                  <v-icon small class="tx-chip__icon">sell</v-icon>
                  {{ serviciosActivos }} tarifas de servicio vigentes
                  <template v-if="serviceRates.length !== serviciosActivos">
                    ({{ serviceRates.length - serviciosActivos }} retiradas)
                  </template>
                </span>
                <span class="tx-chip">
                  <v-icon small class="tx-chip__icon">folder</v-icon>
                  {{ svcFolders.length - 1 }} cliente(s) con carpeta
                </span>
                <span v-if="sinPrecio" class="tx-chip tx-chip--danger">
                  <v-icon small class="tx-chip__icon">error_outline</v-icon>
                  {{ sinPrecio }} en B/. 0.00
                </span>
              </div>
              <div class="tx-toolbar__actions">
                <v-tooltip bottom>
                  <template slot="activator">
                    <button
                      class="tx-btn tx-btn--approve"
                      @click="openNewFolder"
                    >
                      <v-icon small class="tx-btn__icon"
                        >create_new_folder</v-icon
                      >
                      Nueva carpeta
                    </button>
                  </template>
                  <span>
                    Abre la carpeta de un cliente para crearle sus propias
                    tarifas
                  </span>
                </v-tooltip>
              </div>
            </div>
            <v-divider></v-divider>

            <v-card-text class="pa-0">
              <p class="svc-note">
                Cada cliente tiene su carpeta con sus precios; lo que no tenga
                precio propio se cobra con la carpeta General. Un concepto en
                B/. 0.00 no se agrega solo a la factura: hasta que tenga precio,
                contabilidad tiene que teclearlo a mano en cada trámite.
              </p>
              <p v-if="loadingServices" class="empty-state">Cargando...</p>
              <div v-else class="folders">
                <section
                  v-for="f in svcFolders"
                  :key="f.key"
                  class="folder"
                  :class="{ 'folder--open': isOpen(f) }"
                >
                  <header class="folder__head" @click="toggleFolder(f)">
                    <v-icon small class="folder__icon">
                      {{ isOpen(f) ? 'folder_open' : 'folder' }}
                    </v-icon>
                    <div class="folder__name">
                      {{ f.name }}
                      <span v-if="f.owner !== 'GENERAL'" class="folder__tag">
                        {{ f.owner === 'LAB' ? 'Laboratorio' : 'Distribuidor' }}
                      </span>
                      <span v-else class="folder__tag folder__tag--general">
                        Todos los clientes
                      </span>
                    </div>
                    <span class="folder__count">
                      {{ f.active.length }} vigente(s)
                      <template v-if="f.retired.length">
                        · {{ f.retired.length }} retirada(s)
                      </template>
                    </span>
                    <span v-if="f.zeros" class="folder__zero">
                      {{ f.zeros }} en B/. 0.00
                    </span>
                    <button
                      class="tx-btn tx-btn--ghost folder__new"
                      @click.stop="openNewSvc(f)"
                    >
                      <v-icon small class="tx-btn__icon">add</v-icon>
                      Nueva tarifa
                    </button>
                    <v-icon small class="folder__chev">
                      {{ isOpen(f) ? 'expand_less' : 'expand_more' }}
                    </v-icon>
                  </header>

                  <div v-if="isOpen(f)" class="folder__body">
                    <div class="table-wrap">
                      <table class="tx-table">
                        <thead>
                          <tr>
                            <th>Concepto</th>
                            <th class="th-right">Tarifa (B/.)</th>
                            <th>Vigente desde</th>
                            <th class="th-actions">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="rate in visibleSvc(f)"
                            :key="rate.id"
                            :class="{ 'is-retired': !rate.active }"
                          >
                            <td class="tx-cell--strong">
                              {{ rate.concept ? rate.concept.name : '—' }}
                            </td>
                            <td class="th-right">
                              <span
                                :class="{
                                  'svc-zero': !rate.amount && rate.active,
                                }"
                              >
                                {{ money(rate.amount) }}
                              </span>
                            </td>
                            <td class="tx-nowrap">
                              {{ dateOf(rate.validFrom) }}
                            </td>
                            <td class="th-actions">
                              <div class="action-group">
                                <!-- Una tarifa retirada no se edita: editarla
                                     "guardaba" pero seguía inactiva y el
                                     cálculo la ignoraba. Se crea una nueva. -->
                                <span
                                  v-if="!rate.active"
                                  class="status-pill status-pill--muted"
                                >
                                  Retirada
                                </span>
                                <template v-else>
                                  <v-tooltip bottom>
                                    <template slot="activator">
                                      <v-btn
                                        class="btn-detail"
                                        icon
                                        small
                                        aria-label="Editar tarifa"
                                        @click="openEditSvc(rate)"
                                      >
                                        <v-icon small>edit</v-icon>
                                      </v-btn>
                                    </template>
                                    <span>Editar esta tarifa</span>
                                  </v-tooltip>
                                  <v-tooltip bottom>
                                    <template slot="activator">
                                      <v-btn
                                        class="btn-reject"
                                        icon
                                        small
                                        aria-label="Retirar tarifa"
                                        @click="retireSvc(rate)"
                                      >
                                        <v-icon small>block</v-icon>
                                      </v-btn>
                                    </template>
                                    <span>
                                      Retira la tarifa. No se borra: se conserva
                                      para auditar con qué precio se facturó
                                      cada periodo
                                    </span>
                                  </v-tooltip>
                                </template>
                              </div>
                            </td>
                          </tr>
                          <tr v-if="!f.active.length">
                            <td colspan="4" class="empty-state">
                              <template v-if="f.owner === 'GENERAL'">
                                No hay tarifas generales vigentes.
                              </template>
                              <template v-else>
                                Esta carpeta aún no tiene tarifas: este cliente
                                se cobra con la General. Usa «Nueva tarifa» para
                                crear la primera.
                              </template>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <button
                      v-if="f.retired.length"
                      class="retired-link folder__retired"
                      @click="toggleRetired(f)"
                    >
                      {{
                        showRetired[f.key]
                          ? 'Ocultar retiradas'
                          : `Ver ${f.retired.length} retirada(s)`
                      }}
                    </button>
                  </div>
                </section>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- Alta y edición -->
    <v-dialog v-model="form.show" max-width="620" persistent>
      <v-card class="rt">
        <header class="rt__head">
          <div>
            <p class="rt__eyebrow">Tarifa de corretaje</p>
            <h2 class="rt__title">
              {{ form.data.id ? 'Editar tramo' : 'Nuevo tramo' }}
            </h2>
          </div>
          <button
            type="button"
            class="rt__close"
            title="Cerrar"
            @click="form.show = false"
          >
            <v-icon small>close</v-icon>
          </button>
        </header>

        <div class="rt__body">
          <div class="rt__field">
            <label class="rt__label">Nombre del tramo</label>
            <input
              v-model="form.data.name"
              type="text"
              class="rt__input"
              placeholder="Resolución 222/2025 - desde 10,000.01"
            />
          </div>

          <div class="rt__grid">
            <div class="rt__field">
              <label class="rt__label">CIF desde</label>
              <input
                v-model="form.data.cifFrom"
                type="text"
                inputmode="decimal"
                class="rt__input"
                placeholder="0"
              />
            </div>
            <div class="rt__field">
              <label class="rt__label">CIF hasta</label>
              <input
                v-model="form.data.cifTo"
                type="text"
                inputmode="decimal"
                class="rt__input"
                placeholder="En blanco = sin tope"
              />
            </div>
          </div>

          <div class="rt__grid">
            <div class="rt__field">
              <label class="rt__label">Tarifa fija (B/.)</label>
              <input
                v-model="form.data.baseAmount"
                type="text"
                inputmode="decimal"
                class="rt__input"
                placeholder="80.00"
              />
            </div>
            <div class="rt__field">
              <label class="rt__label">Porcentaje sobre el CIF</label>
              <input
                v-model="form.data.percent"
                type="text"
                inputmode="decimal"
                class="rt__input"
                placeholder="0.0027"
              />
              <p class="rt__help">
                En decimal: 0.0027 es 0,27%. Deja 0 si el tramo es sólo tarifa
                fija.
              </p>
            </div>
          </div>

          <div class="rt__grid">
            <div class="rt__field">
              <label class="rt__label">El % aplica sobre el excedente de</label>
              <input
                v-model="form.data.percentOverAmount"
                type="text"
                inputmode="decimal"
                class="rt__input"
                placeholder="0"
              />
              <p class="rt__help">
                0 = el porcentaje se calcula sobre el CIF completo. Con 5000 se
                calcula sólo sobre lo que exceda de B/. 5,000.
              </p>
            </div>
            <div class="rt__field">
              <label class="rt__label">Cargo por línea adicional</label>
              <input
                v-model="form.data.perExtraLine"
                type="text"
                inputmode="decimal"
                class="rt__input"
                placeholder="5.00"
              />
              <p class="rt__help">
                Por cada línea de aforo después de la primera.
              </p>
            </div>
          </div>

          <div class="rt__grid">
            <div class="rt__field">
              <label class="rt__label">¿De quién es la tarifa?</label>
              <v-select
                v-model="form.data.owner"
                :items="owners"
                item-text="text"
                item-value="value"
                solo
                flat
                hide-details
                class="rt__select"
                @change="onOwnerChange(form)"
              ></v-select>
            </div>
            <div v-if="form.data.owner !== 'GENERAL'" class="rt__field">
              <label class="rt__label">{{ ownerLabel(form) }}</label>
              <v-select
                v-model="customsClientId"
                :items="ownerItems(form)"
                item-text="name"
                item-value="id"
                placeholder="Elige el cliente"
                no-data-text="Datos no disponibles"
                clearable
                solo
                flat
                hide-details
                class="rt__select"
              ></v-select>
            </div>
          </div>
          <p class="rt__help">
            La tarifa es general, de un laboratorio o de un distribuidor: nunca
            de los dos a la vez. La de un cliente gana sobre la general.
          </p>

          <div class="rt__field">
            <label class="rt__label">Vigente desde</label>
            <input
              v-model="form.data.validFrom"
              type="date"
              class="rt__input"
            />
            <p class="rt__help">
              Los trámites ya calculados conservan su monto: la fecha sólo
              decide qué tramo se usa de aquí en adelante.
            </p>
          </div>

          <p v-if="form.error" class="rt__error">{{ form.error }}</p>
        </div>

        <footer class="rt__foot">
          <button class="tx-btn tx-btn--ghost" @click="form.show = false">
            Cancelar
          </button>
          <button
            class="tx-btn tx-btn--approve"
            :disabled="form.saving"
            @click="save"
          >
            {{ form.saving ? 'Guardando…' : 'Guardar tramo' }}
          </button>
        </footer>
      </v-card>
    </v-dialog>

    <!-- Alta y edición de una tarifa de servicio -->
    <v-dialog v-model="svcForm.show" max-width="560" persistent>
      <v-card class="rt">
        <header class="rt__head">
          <div>
            <p class="rt__eyebrow">Tarifa de servicio</p>
            <h2 class="rt__title">
              {{ svcForm.data.id ? 'Editar tarifa' : 'Nueva tarifa' }}
            </h2>
          </div>
          <button
            type="button"
            class="rt__close"
            title="Cerrar"
            @click="svcForm.show = false"
          >
            <v-icon small>close</v-icon>
          </button>
        </header>

        <div class="rt__body">
          <div class="rt__field">
            <label class="rt__label">Concepto</label>
            <v-select
              v-model="svcForm.data.conceptId"
              :items="concepts"
              :disabled="!!svcForm.data.id"
              item-text="name"
              item-value="id"
              placeholder="Elige el concepto"
              no-data-text="Sin conceptos disponibles"
              solo
              flat
              hide-details
              class="rt__select"
            ></v-select>
            <p v-if="svcForm.data.id" class="rt__help">
              El concepto no se cambia: crea otra tarifa si es para uno
              distinto.
            </p>
          </div>

          <div class="rt__field">
            <label class="rt__label">Tarifa (B/.)</label>
            <input
              v-model="svcForm.data.amount"
              type="text"
              inputmode="decimal"
              class="rt__input"
              placeholder="35.00"
            />
          </div>

          <!-- Desde una carpeta el dueño viene fijo: la tarifa se guarda en
               esa carpeta y no se puede mandar a otra por error. -->
          <div v-if="svcForm.lockOwner" class="rt__folder">
            <v-icon small class="rt__folder-icon">folder</v-icon>
            <span>
              Carpeta <strong>{{ svcForm.folderName }}</strong>
            </span>
          </div>
          <div v-else class="rt__grid">
            <div class="rt__field">
              <label class="rt__label">¿De quién es la tarifa?</label>
              <v-select
                v-model="svcForm.data.owner"
                :items="owners"
                item-text="text"
                item-value="value"
                solo
                flat
                hide-details
                class="rt__select"
                @change="onOwnerChange(svcForm)"
              ></v-select>
            </div>
            <div v-if="svcForm.data.owner !== 'GENERAL'" class="rt__field">
              <label class="rt__label">{{ ownerLabel(svcForm) }}</label>
              <v-select
                v-model="svcClientId"
                :items="ownerItems(svcForm)"
                item-text="name"
                item-value="id"
                placeholder="Elige el cliente"
                no-data-text="Datos no disponibles"
                clearable
                solo
                flat
                hide-details
                class="rt__select"
              ></v-select>
            </div>
          </div>
          <p class="rt__help">
            <template v-if="svcForm.lockOwner">
              Para otro cliente, abre su carpeta. La tarifa de un cliente gana
              sobre la general.
            </template>
            <template v-else>
              La tarifa es general, de un laboratorio o de un distribuidor:
              nunca de los dos a la vez. La de un cliente gana sobre la general.
            </template>
          </p>

          <div class="rt__field">
            <label class="rt__label">Vigente desde</label>
            <input
              v-model="svcForm.data.validFrom"
              type="date"
              class="rt__input"
            />
            <p class="rt__help">
              Lo ya facturado conserva su monto: la fecha sólo decide qué precio
              se usa de aquí en adelante.
            </p>
          </div>

          <p v-if="svcForm.error" class="rt__error">{{ svcForm.error }}</p>
        </div>

        <footer class="rt__foot">
          <button class="tx-btn tx-btn--ghost" @click="svcForm.show = false">
            Cancelar
          </button>
          <button
            class="tx-btn tx-btn--approve"
            :disabled="svcForm.saving"
            @click="saveSvc"
          >
            {{ svcForm.saving ? 'Guardando…' : 'Guardar tarifa' }}
          </button>
        </footer>
      </v-card>
    </v-dialog>

    <!-- Nueva carpeta: elegir el cliente y pasar directo a su primera tarifa -->
    <v-dialog v-model="folderForm.show" max-width="520" persistent>
      <v-card class="rt">
        <header class="rt__head">
          <div>
            <p class="rt__eyebrow">Tarifas de servicio</p>
            <h2 class="rt__title">Nueva carpeta</h2>
          </div>
          <button
            type="button"
            class="rt__close"
            title="Cerrar"
            @click="folderForm.show = false"
          >
            <v-icon small>close</v-icon>
          </button>
        </header>

        <div class="rt__body">
          <div class="rt__field">
            <label class="rt__label">Tipo de cliente</label>
            <v-select
              v-model="folderForm.owner"
              :items="folderOwners"
              item-text="text"
              item-value="value"
              solo
              flat
              hide-details
              class="rt__select"
              @change="folderForm.clientId = null"
            ></v-select>
          </div>
          <div class="rt__field">
            <label class="rt__label">
              {{ folderForm.owner === 'LAB' ? 'Laboratorio' : 'Distribuidor' }}
            </label>
            <v-select
              v-model="folderForm.clientId"
              :items="folderCandidates"
              item-text="name"
              item-value="id"
              placeholder="Elige el cliente"
              no-data-text="Todos los clientes de este tipo ya tienen carpeta"
              solo
              flat
              hide-details
              class="rt__select"
            ></v-select>
            <p class="rt__help">
              La carpeta lleva el nombre del cliente. Al crearla se abre la
              primera tarifa; hasta que la guardes, ese cliente sigue cobrándose
              con la General.
            </p>
          </div>
          <p v-if="folderForm.error" class="rt__error">
            {{ folderForm.error }}
          </p>
        </div>

        <footer class="rt__foot">
          <button class="tx-btn tx-btn--ghost" @click="folderForm.show = false">
            Cancelar
          </button>
          <button class="tx-btn tx-btn--approve" @click="confirmNewFolder">
            <v-icon small class="tx-btn__icon">create_new_folder</v-icon>
            Crear carpeta
          </button>
        </footer>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snack.show"
      :timeout="4000"
      :color="snack.color"
      right
      top
    >
      {{ snack.message }}
      <v-btn flat dark @click="snack.show = false">Cerrar</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import moment from 'moment-timezone'

// Una tarifa es general, o de UN laboratorio, o de UN distribuidor. Nunca de
// los dos: la consulta que la elige hace OR entre labId y distId, así que una
// fila con ambos se aplicaría a cualquier trámite de ese laboratorio aunque el
// distribuidor fuera otro, y al revés. `owner` vive solo en el formulario; al
// backend siguen viajando labId y distId, con el que no aplica en null.
const OWNERS = [
  { value: 'GENERAL', text: 'General (todos los clientes)' },
  { value: 'LAB', text: 'Un laboratorio' },
  { value: 'DIST', text: 'Un distribuidor' },
]

// Dueño de una fila ya guardada. Si viene con los dos puestos (dato heredado de
// cuando el formulario lo permitía) gana el laboratorio, que es justo el que el
// listado ya venía mostrando.
const ownerOf = rate => {
  if (rate.labId) return 'LAB'
  if (rate.distId) return 'DIST'
  return 'GENERAL'
}

const clientIdOfRate = rate => {
  const owner = ownerOf(rate)
  if (owner === 'LAB') return rate.labId
  if (owner === 'DIST') return rate.distId
  return null
}

const folderKey = (owner, clientId) =>
  owner === 'GENERAL' ? 'GENERAL' : `${owner}-${clientId}`

const clientNameOf = (rate, clientId) =>
  rate.laboratorio || rate.distribuidor || `Cliente #${clientId}`

const newFolder = (owner, clientId, name) => ({
  key: folderKey(owner, clientId),
  owner,
  clientId,
  name,
  active: [],
  retired: [],
  zeros: 0,
})

const byName = (a, b) =>
  String(a.name).localeCompare(String(b.name), 'es', { sensitivity: 'base' })

const byConceptOrder = (a, b) => {
  const oa = a.concept ? a.concept.order || 0 : 0
  const ob = b.concept ? b.concept.order || 0 : 0
  if (oa !== ob) return oa - ob
  return String(b.validFrom).localeCompare(String(a.validFrom))
}

const byValidFromDesc = (a, b) =>
  String(b.validFrom).localeCompare(String(a.validFrom))

// Vigentes primero; dentro de cada bloque por CIF inicial y luego por vigencia.
const byCifFrom = (a, b) => {
  if (a.active !== b.active) return a.active ? -1 : 1
  const d = parseFloat(a.cifFrom) - parseFloat(b.cifFrom)
  if (d !== 0) return d
  return String(b.validFrom).localeCompare(String(a.validFrom))
}

const EMPTY_SVC = {
  id: null,
  conceptId: null,
  amount: '0',
  owner: 'GENERAL',
  labId: null,
  distId: null,
  validFrom: '',
  active: true,
}

const EMPTY = {
  id: null,
  name: '',
  cifFrom: '0',
  cifTo: '',
  baseAmount: '0',
  percent: '0',
  percentOverAmount: '0',
  perExtraLine: '0',
  owner: 'GENERAL',
  labId: null,
  distId: null,
  validFrom: '',
  active: true,
}

export default {
  data() {
    return {
      rates: [],
      loadingRates: true,
      laboratories: [],
      distributors: [],
      form: { show: false, saving: false, error: '', data: { ...EMPTY } },
      serviceRates: [],
      concepts: [],
      loadingServices: true,
      svcForm: {
        show: false,
        saving: false,
        error: '',
        // Desde una carpeta el dueño viene fijo y el diálogo lo muestra, no
        // lo pregunta.
        lockOwner: false,
        folderName: '',
        data: { ...EMPTY_SVC },
      },
      // Carpetas: qué cliente está abierto y en cuáles se ven las retiradas.
      // Vacío = solo General abierta, retiradas plegadas.
      openFolders: {},
      showRetired: {},
      showRetiredRates: false,
      // Carpetas abiertas a mano que aún no tienen ninguna tarifa guardada.
      extraFolders: [],
      folderForm: { show: false, owner: 'LAB', clientId: null, error: '' },
      // Simulador: se calcula en el cliente con los mismos tramos que usa el
      // backend, para poder probar sin guardar nada.
      sim: {
        cif: '',
        lines: '1',
        result: null,
        rateName: '',
        error: '',
        detail: [],
      },
      snack: { show: false, color: 'info', message: '' },
    }
  },
  created() {
    this.load()
    this.loadClients()
    this.loadServices()
  },
  computed: {
    owners() {
      return OWNERS
    },
    // Un solo campo de cliente para los dos posibles dueños: el getter lee el
    // id que corresponde al `owner` elegido y el setter deja el otro en null,
    // así el formulario no puede producir una fila amarrada a ambos.
    customsClientId: {
      get() {
        return this.clientIdOf(this.form)
      },
      set(value) {
        this.setClientId(this.form, value)
      },
    },
    svcClientId: {
      get() {
        return this.clientIdOf(this.svcForm)
      },
      set(value) {
        this.setClientId(this.svcForm, value)
      },
    },
    activos() {
      return this.rates.filter(r => r.active).length
    },
    serviciosActivos() {
      return this.serviceRates.filter(r => r.active).length
    },
    // Una tarifa vigente en 0 es un concepto que nunca se va a facturar solo.
    sinPrecio() {
      return this.serviceRates.filter(r => r.active && !r.amount).length
    },
    folderOwners() {
      return [
        { value: 'LAB', text: 'Laboratorio' },
        { value: 'DIST', text: 'Distribuidor' },
      ]
    },
    // Una carpeta por dueño: General primero y después cada cliente con su
    // nombre. Dentro, las vigentes en el orden del catálogo y las retiradas
    // aparte, de la más reciente a la más vieja.
    svcFolders() {
      const byKey = {}
      const general = newFolder('GENERAL', null, 'General')
      byKey[general.key] = general
      this.serviceRates.forEach(rate => {
        const owner = ownerOf(rate)
        const clientId = clientIdOfRate(rate)
        const key = folderKey(owner, clientId)
        if (!byKey[key]) {
          byKey[key] = newFolder(owner, clientId, clientNameOf(rate, clientId))
        }
        const f = byKey[key]
        if (rate.active) {
          f.active.push(rate)
          if (!rate.amount) f.zeros += 1
        } else {
          f.retired.push(rate)
        }
      })
      this.extraFolders.forEach(x => {
        if (!byKey[x.key]) byKey[x.key] = newFolder(x.owner, x.clientId, x.name)
      })
      const clientes = Object.keys(byKey)
        .filter(k => k !== 'GENERAL')
        .map(k => byKey[k])
        .sort(byName)
      const all = [general].concat(clientes)
      all.forEach(f => {
        f.active.sort(byConceptOrder)
        f.retired.sort(byValidFromDesc)
      })
      return all
    },
    // Tramos de corretaje agrupados igual: General y luego cada cliente. Un
    // grupo que solo tenga tramos retirados se pliega con ellos.
    ratesGrouped() {
      const byKey = {}
      this.rates.forEach(rate => {
        const owner = ownerOf(rate)
        const clientId = clientIdOfRate(rate)
        const key = folderKey(owner, clientId)
        if (!byKey[key]) {
          byKey[key] = {
            key,
            owner,
            clientId,
            name:
              owner === 'GENERAL' ? 'General' : clientNameOf(rate, clientId),
            rates: [],
          }
        }
        byKey[key].rates.push(rate)
      })
      const general = byKey.GENERAL ? [byKey.GENERAL] : []
      const clientes = Object.keys(byKey)
        .filter(k => k !== 'GENERAL')
        .map(k => byKey[k])
        .sort(byName)
      return general
        .concat(clientes)
        .filter(g => this.showRetiredRates || g.rates.some(r => r.active))
        .map(g => ({ ...g, rates: g.rates.slice().sort(byCifFrom) }))
    },
    // Clientes del tipo elegido que todavía no tienen carpeta.
    folderCandidates() {
      const owner = this.folderForm.owner
      const source = owner === 'LAB' ? this.laboratories : this.distributors
      const taken = new Set(this.svcFolders.map(f => f.key))
      return source.filter(c => !taken.has(folderKey(owner, c.id)))
    },
  },
  methods: {
    // --- Dueño de la tarifa: general / laboratorio / distribuidor ---
    ownerLabel(form) {
      return form.data.owner === 'LAB' ? 'Laboratorio' : 'Distribuidor'
    },
    ownerItems(form) {
      return form.data.owner === 'LAB' ? this.laboratories : this.distributors
    },
    clientIdOf(form) {
      if (form.data.owner === 'LAB') return form.data.labId
      if (form.data.owner === 'DIST') return form.data.distId
      return null
    },
    setClientId(form, value) {
      const d = form.data
      if (d.owner === 'LAB') {
        d.labId = value === undefined ? null : value
        d.distId = null
      } else if (d.owner === 'DIST') {
        d.distId = value === undefined ? null : value
        d.labId = null
      }
    },
    // Cambiar de dueño borra el id anterior: si no, una tarifa que pasa de
    // laboratorio a distribuidor viajaría con los dos puestos.
    onOwnerChange(form) {
      const d = form.data
      if (d.owner === 'GENERAL') {
        d.labId = null
        d.distId = null
      } else if (d.owner === 'LAB') {
        d.distId = null
      } else {
        d.labId = null
      }
    },
    money(value) {
      const n = parseFloat(value)
      if (Number.isNaN(n)) return '—'
      return n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    dateOf(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '—'
    },
    notify(message, color = 'info') {
      this.snack = { show: true, color, message }
    },
    load() {
      this.loadingRates = true
      this.$store
        .dispatch('invoices/getCustomsRates')
        .then(rates => {
          this.rates = rates || []
        })
        .finally(() => {
          this.loadingRates = false
        })
    },
    loadClients() {
      this.$store.dispatch('laboratories/getAll', {}).then(response => {
        const data = (response && response.items) || []
        this.laboratories = data.map(l => ({ name: l.name, id: l.id }))
      })
      this.$store.dispatch('distributors/getAll', {}).then(response => {
        const data = (response && response.items) || []
        this.distributors = data.map(d => ({ name: d.name, id: d.id }))
      })
    },
    openNew() {
      this.form = {
        show: true,
        saving: false,
        error: '',
        data: { ...EMPTY, validFrom: moment().format('YYYY-MM-DD') },
      }
    },
    openEdit(rate) {
      this.form = {
        show: true,
        saving: false,
        error: '',
        data: {
          id: rate.id,
          name: rate.name,
          cifFrom: String(rate.cifFrom),
          cifTo: rate.cifTo === null ? '' : String(rate.cifTo),
          baseAmount: String(rate.baseAmount),
          percent: String(rate.percent),
          percentOverAmount: String(rate.percentOverAmount),
          perExtraLine: String(rate.perExtraLine),
          owner: ownerOf(rate),
          // Se normaliza al abrir: una fila heredada con laboratorio Y
          // distribuidor se muestra por su laboratorio y guarda así, en vez de
          // volver a viajar amarrada a los dos.
          labId: ownerOf(rate) === 'LAB' ? rate.labId : null,
          distId: ownerOf(rate) === 'DIST' ? rate.distId : null,
          validFrom: rate.validFrom
            ? moment(rate.validFrom).format('YYYY-MM-DD')
            : '',
          active: rate.active,
        },
      }
    },
    save() {
      const d = this.form.data
      if (!d.name || !d.name.trim()) {
        this.form.error = 'El tramo necesita un nombre.'
        return
      }
      const from = parseFloat(d.cifFrom || 0)
      const to = d.cifTo === '' ? null : parseFloat(d.cifTo)
      if (to !== null && !Number.isNaN(to) && to < from) {
        this.form.error = 'El CIF final no puede ser menor que el inicial.'
        return
      }
      this.form.error = ''
      this.form.saving = true
      this.$store
        .dispatch('invoices/saveCustomsRate', d)
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.form.error = 'No se pudo guardar el tramo.'
            return
          }
          this.form.show = false
          this.load()
          this.notify('Tramo guardado.', 'success')
        })
        .finally(() => {
          this.form.saving = false
        })
    },
    retire(rate) {
      this.$store
        .dispatch('invoices/deleteCustomsRate', { id: rate.id })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.notify('No se pudo retirar el tramo.', 'error')
            return
          }
          this.load()
          this.notify(`Tramo "${rate.name}" retirado.`, 'primary')
        })
    },
    loadServices() {
      this.loadingServices = true
      Promise.all([
        this.$store.dispatch('invoices/getServiceRates'),
        this.$store.dispatch('invoices/getServiceConcepts'),
      ])
        .then(([rates, concepts]) => {
          this.serviceRates = rates || []
          // Los conceptos de cálculo automático sacan su monto del CIF, del
          // reembolso o de la cantidad: fijarles un precio no haría nada.
          this.concepts = (concepts || []).filter(
            c => c.calcType === 'FIXED' || c.calcType === 'FORMS'
          )
        })
        .finally(() => {
          this.loadingServices = false
        })
    },
    // --- Carpetas ---
    isOpen(f) {
      const v = this.openFolders[f.key]
      return v === undefined ? f.owner === 'GENERAL' : v
    },
    toggleFolder(f) {
      this.$set(this.openFolders, f.key, !this.isOpen(f))
    },
    toggleRetired(f) {
      this.$set(this.showRetired, f.key, !this.showRetired[f.key])
    },
    visibleSvc(f) {
      return this.showRetired[f.key] ? f.active.concat(f.retired) : f.active
    },
    visibleRates(g) {
      return this.showRetiredRates ? g.rates : g.rates.filter(r => r.active)
    },
    openNewFolder() {
      this.folderForm = { show: true, owner: 'LAB', clientId: null, error: '' }
    },
    // La carpeta existe en pantalla desde ya, aunque quede vacía hasta que se
    // guarde la primera tarifa; y se pasa directo a crearla.
    confirmNewFolder() {
      const { owner, clientId } = this.folderForm
      if (!clientId) {
        this.folderForm.error = 'Elige el cliente.'
        return
      }
      const source = owner === 'LAB' ? this.laboratories : this.distributors
      const client = source.find(c => c.id === clientId)
      const key = folderKey(owner, clientId)
      if (!this.svcFolders.some(f => f.key === key)) {
        this.extraFolders.push({
          key,
          owner,
          clientId,
          name: client ? client.name : `Cliente #${clientId}`,
        })
      }
      this.$set(this.openFolders, key, true)
      this.folderForm.show = false
      this.openNewSvc(this.svcFolders.find(f => f.key === key))
    },
    // Nueva tarifa dentro de una carpeta: el dueño ya está decidido.
    openNewSvc(folder) {
      const owner = folder ? folder.owner : 'GENERAL'
      this.svcForm = {
        show: true,
        saving: false,
        error: '',
        lockOwner: true,
        folderName: folder ? folder.name : 'General',
        data: {
          ...EMPTY_SVC,
          owner,
          labId: owner === 'LAB' ? folder.clientId : null,
          distId: owner === 'DIST' ? folder.clientId : null,
          validFrom: moment().format('YYYY-MM-DD'),
        },
      }
    },
    openEditSvc(rate) {
      this.svcForm = {
        show: true,
        saving: false,
        error: '',
        lockOwner: false,
        folderName: '',
        data: {
          id: rate.id,
          conceptId: rate.conceptId,
          amount: String(rate.amount),
          owner: ownerOf(rate),
          // Igual que en el tramo de corretaje: se normaliza al abrir.
          labId: ownerOf(rate) === 'LAB' ? rate.labId : null,
          distId: ownerOf(rate) === 'DIST' ? rate.distId : null,
          validFrom: rate.validFrom
            ? moment(rate.validFrom).format('YYYY-MM-DD')
            : '',
          active: rate.active,
        },
      }
    },
    saveSvc() {
      const d = this.svcForm.data
      if (!d.conceptId) {
        this.svcForm.error = 'Elige el concepto.'
        return
      }
      const amount = parseFloat(d.amount)
      if (Number.isNaN(amount) || amount < 0) {
        this.svcForm.error = 'La tarifa debe ser un monto válido.'
        return
      }
      this.svcForm.error = ''
      this.svcForm.saving = true
      this.$store
        .dispatch('invoices/saveServiceRate', d)
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.svcForm.error = 'No se pudo guardar la tarifa.'
            return
          }
          this.svcForm.show = false
          this.loadServices()
          this.notify('Tarifa guardada.', 'success')
        })
        .finally(() => {
          this.svcForm.saving = false
        })
    },
    retireSvc(rate) {
      this.$store
        .dispatch('invoices/deleteServiceRate', { id: rate.id })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.notify('No se pudo retirar la tarifa.', 'error')
            return
          }
          this.loadServices()
          const name = rate.concept ? rate.concept.name : 'La tarifa'
          this.notify(`Tarifa de "${name}" retirada.`, 'primary')
        })
    },
    // Réplica exacta de la fórmula del backend, para probar sin guardar.
    simulate() {
      const cif = parseFloat(this.sim.cif)
      const lines = parseInt(this.sim.lines, 10) || 1
      this.sim.result = null
      this.sim.rateName = ''
      this.sim.error = ''
      this.sim.detail = []
      if (Number.isNaN(cif) || cif < 0) {
        this.sim.error = 'Escribe un valor CIF válido.'
        return
      }
      const today = moment().format('YYYY-MM-DD')
      const candidatos = this.rates.filter(
        r =>
          r.active &&
          moment(r.validFrom).format('YYYY-MM-DD') <= today &&
          r.cifFrom <= cif &&
          (r.cifTo === null || r.cifTo >= cif) &&
          !r.labId &&
          !r.distId
      )
      if (!candidatos.length) {
        this.sim.error = 'Ningún tramo general aplica a ese CIF.'
        return
      }
      candidatos.sort((a, b) =>
        String(b.validFrom).localeCompare(String(a.validFrom))
      )
      const r = candidatos[0]
      const extraLines = Math.max(0, lines - 1)
      const gravable = Math.max(0, cif - r.percentOverAmount)
      const porcentaje = gravable * r.percent
      const porLineas = r.perExtraLine * extraLines
      const total = r.baseAmount + porcentaje + porLineas
      this.sim.result = Math.round((total + Number.EPSILON) * 100) / 100
      this.sim.rateName = r.name
      const bal = v => `B/. ${this.money(v)}`
      const base = r.percentOverAmount
        ? `el excedente de ${bal(r.percentOverAmount)} (${bal(gravable)})`
        : `el CIF completo (${bal(cif)})`
      this.sim.detail = [
        { label: 'Tramo aplicado', value: r.name },
        { label: 'Tarifa base', value: bal(r.baseAmount) },
        {
          label: r.percent
            ? `${this.pct(r.percent)} sobre ${base}`
            : 'Porcentaje sobre el CIF',
          value: r.percent ? bal(porcentaje) : 'No aplica en este tramo',
        },
        {
          label:
            `Líneas adicionales: ${lines} línea(s), la primera incluida ` +
            `= ${extraLines} × ${bal(r.perExtraLine)}`,
          value: bal(porLineas),
        },
      ]
    },
    pct(value) {
      const n = (parseFloat(value) || 0) * 100
      return `${n.toLocaleString('en-US', { maximumFractionDigits: 4 })} %`
    },
  },
}
</script>

<style scoped src="./../transit/transit-list.css"></style>

<style scoped>
/* Aviso de la tabla de tarifas de servicio: informativo, no una alarma. */
.svc-note {
  margin: 0;
  padding: 10px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  color: #475569;
  font-size: 12px;
  line-height: 1.5;
}

/* Un precio en cero es lo que hay que corregir: se marca, sin gritar. */
.svc-zero {
  color: #b45309;
  font-weight: 700;
}

/* Tablas de contabilidad: 15 px en celdas y 13 px en encabezados, como en
   las otras pantallas del módulo. */
.tx-table th,
.tx-table td {
  font-size: 15px;
  padding: 12px 14px;
}
.tx-table th {
  font-size: 13px;
}
.tx-chip--danger {
  background: rgba(220, 38, 38, 0.12);
  color: #991b1b;
}

/* Lo retirado se pliega detrás de un enlace: se conserva para auditar, pero
   no estorba en el día a día. */
.retired-link {
  border: none;
  background: transparent;
  padding: 0 4px;
  font-size: 13px;
  font-weight: 600;
  color: #254f87;
  cursor: pointer;
  text-decoration: underline dotted;
}
.retired-link:hover {
  color: #1e3f6b;
}

/* Grupo por dueño en la tabla de tramos */
.grp-row td {
  background: #f1f5f9 !important;
  font-size: 13px !important;
  font-weight: 700;
  color: #334155;
  padding: 8px 14px !important;
}
.grp-row__icon {
  color: #64748b !important;
  font-size: 16px !important;
  margin-right: 4px;
  vertical-align: -3px;
}
.grp-row__count {
  margin-left: 8px;
  font-weight: 500;
  color: #64748b;
}

/* === Carpetas por cliente === */
.folders {
  display: flex;
  flex-direction: column;
}
.folder {
  border-bottom: 1px solid #eef2f7;
}
.folder__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease;
}
.folder__head:hover,
.folder--open .folder__head {
  background: #f8fafc;
}
.folder__icon {
  color: #254f87 !important;
  font-size: 20px !important;
}
.folder__name {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}
.folder__tag {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(37, 79, 135, 0.1);
  color: #254f87;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}
.folder__tag--general {
  background: rgba(148, 163, 184, 0.18);
  color: #64748b;
}
.folder__count {
  font-size: 13px;
  color: #64748b;
  white-space: nowrap;
}
.folder__zero {
  font-size: 12px;
  font-weight: 700;
  color: #b45309;
  white-space: nowrap;
}
.folder__new {
  height: 32px;
  padding: 0 12px;
  font-size: 13px;
}
.folder__chev {
  color: #94a3b8 !important;
}
.folder__body {
  padding: 0 0 8px;
  background: #ffffff;
}
.folder__body .tx-table thead tr {
  background: #ffffff;
}
.folder__retired {
  margin: 6px 16px 4px;
}

/* === Simulador: desglose === */
.sim__detail {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 24px;
  margin: 12px 0 0;
  padding: 10px 14px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  max-width: 760px;
}
.sim__detail-label {
  font-size: 13px;
  color: #475569;
}
.sim__detail-value {
  margin: 0;
  font-size: 13px;
  color: #0f172a;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.sim__detail-label--total,
.sim__detail-value--total {
  padding-top: 6px;
  border-top: 1px solid #e2e8f0;
  font-weight: 700;
}
.sim__hint {
  margin: 10px 0 0;
  font-size: 12.5px;
  color: #64748b;
}

/* Diálogo: la carpeta viene fija */
.rt__folder {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f1f5f9;
  font-size: 13px;
  color: #334155;
}
.rt__folder-icon {
  color: #254f87 !important;
}
</style>

<style scoped>
.th-right {
  text-align: right;
}
.is-retired td {
  opacity: 0.55;
}

/* === Simulador === */
.sim {
  padding: 14px 16px;
}
.sim__row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}
.sim__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sim__field--small {
  width: 120px;
}
.sim__label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.sim__input {
  height: 38px;
  width: 180px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  color: #0f172a;
  background: #ffffff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.sim__field--small .sim__input {
  width: 100%;
}
.sim__input:focus {
  outline: none;
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.12);
}
.sim__result {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 8px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}
.sim__result-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #15803d;
}
.sim__result-value {
  font-size: 18px;
  color: #14532d;
  font-variant-numeric: tabular-nums;
}
.sim__result-rate {
  font-size: 12px;
  color: #64748b;
}
.sim__error {
  padding: 8px 14px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  font-size: 12px;
  color: #991b1b;
}

/* === Diálogo === */
.rt {
  border-radius: 12px;
  overflow: hidden;
}
.rt__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px 24px;
  border-left: 4px solid #15803d;
  border-bottom: 1px solid #e2e8f0;
}
.rt__eyebrow {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}
.rt__title {
  margin: 2px 0 0;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
}
.rt__close {
  border: none;
  background: transparent;
  border-radius: 6px;
  padding: 4px;
  cursor: pointer;
  line-height: 1;
}
.rt__close:hover {
  background: #f1f5f9;
}
.rt__close .v-icon {
  color: #64748b !important;
}
.rt__body {
  padding: 16px 24px 4px;
  max-height: 62vh;
  overflow-y: auto;
}
.rt__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.rt__field {
  margin-bottom: 14px;
}
.rt__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 5px;
}
.rt__input {
  width: 100%;
  height: 38px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  color: #0f172a;
  background: #ffffff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.rt__input:focus {
  outline: none;
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.12);
}
.rt__help {
  margin: 4px 0 0;
  font-size: 11.5px;
  color: #94a3b8;
  line-height: 1.4;
}
.rt__error {
  margin: 4px 0 12px;
  font-size: 12.5px;
  color: #b91c1c;
}
.rt__foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 24px 18px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

@media (max-width: 599px) {
  .rt__grid {
    grid-template-columns: 1fr;
  }
}
</style>
